import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Search, TrendingUp, Building2, BookOpen, DollarSign, Wrench, Brain, Briefcase, ArrowRight, Loader2, Mail, MapPin, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav } from '../components/MarketingLayout';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  "Nationwide", "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois",
  "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland",
  "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana",
  "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York",
  "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah",
  "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

function BarChart({ data, maxVal, color = '#0F392B' }) {
  const max = maxVal || Math.max(...data.map(d => d.value));
  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-32 truncate text-right">{item.label}</span>
          <div className="flex-1 h-6 bg-[#F2F2F0] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700 ease-out"
              style={{ width: `${(item.value / max) * 100}%`, backgroundColor: color, opacity: 0.7 + (item.value / max) * 0.3 }} />
          </div>
          <span className="text-xs font-medium w-12">{item.suffix || ''}{item.value}{item.postfix || ''}</span>
        </div>
      ))}
    </div>
  );
}

function EarningsCard({ earnings }) {
  if (!earnings) return null;
  const levels = [
    { label: 'Entry Level', value: earnings.entry_level, color: '#94a3b8' },
    { label: 'Median', value: earnings.median, color: '#0F392B' },
    { label: 'Experienced', value: earnings.experienced, color: '#D4AF37' },
    { label: 'Top 10%', value: earnings.top_10_percent, color: '#16a34a' },
  ];
  const max = earnings.top_10_percent;
  return (
    <div className="space-y-3">
      {levels.map((l, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-24">{l.label}</span>
          <div className="flex-1 h-7 bg-[#F2F2F0] rounded-full overflow-hidden relative">
            <div className="h-full rounded-full transition-all duration-700"
              style={{ width: `${(l.value / max) * 100}%`, backgroundColor: l.color }} />
            <span className="absolute inset-0 flex items-center pl-3 text-[11px] font-semibold text-white mix-blend-difference">
              ${(l.value / 1000).toFixed(0)}k
            </span>
          </div>
        </div>
      ))}
      {earnings.description && <p className="text-xs text-muted-foreground italic mt-2">{earnings.description}</p>}
    </div>
  );
}

function TrendChart({ yearData }) {
  if (!yearData || yearData.length === 0) return null;
  const maxJobs = Math.max(...yearData.map(d => d.jobs));
  const minJobs = Math.min(...yearData.map(d => d.jobs));
  const range = maxJobs - minJobs || 1;
  const w = 100;
  const h = 40;
  const points = yearData.map((d, i) => {
    const x = (i / (yearData.length - 1)) * w;
    const y = h - ((d.jobs - minJobs) / range) * h;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${w} ${h + 4}`} className="w-full h-24" preserveAspectRatio="none">
        <defs>
          <linearGradient id="trendGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0F392B" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#0F392B" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={`0,${h + 4} ${points} ${w},${h + 4}`} fill="url(#trendGrad)" />
        <polyline points={points} fill="none" stroke="#0F392B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {yearData.map((d, i) => {
          const x = (i / (yearData.length - 1)) * w;
          const y = h - ((d.jobs - minJobs) / range) * h;
          return <circle key={i} cx={x} cy={y} r="1.5" fill="#0F392B" />;
        })}
      </svg>
      <div className="flex justify-between mt-1">
        {yearData.map((d, i) => (
          <span key={i} className="text-[9px] text-muted-foreground">{d.year}</span>
        ))}
      </div>
    </div>
  );
}

export default function CareerInsightsPage() {
  const [keyword, setKeyword] = useState('');
  const [state, setState] = useState('Nationwide');
  const [email, setEmail] = useState('');
  const [emailCaptured, setEmailCaptured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!keyword.trim()) { toast.error('Enter a career or occupation'); return; }
    if (!emailCaptured && !email.trim()) { toast.error('Enter your email to access insights'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/career-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: keyword.trim(), state, email: emailCaptured ? null : email }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Search failed'); }
      const result = await res.json();
      setData(result);
      if (!emailCaptured && email) setEmailCaptured(true);
    } catch (err) {
      toast.error(err.message || 'Failed to load career data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-16" data-testid="career-insights-page">
      {/* Nav */}
      <MarketingNav />

      {/* Hero */}
      <section className="bg-[#0F392B] text-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 mb-4 text-xs">Free Career Research Tool</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Let Us Help You Prep Before<br />Taking the First Step
          </h1>
          <p className="text-base text-white/70 max-w-2xl mx-auto leading-relaxed mb-8">
            Use real employment data to explore career paths, understand salary expectations, and identify the skills and education you'll need. Make informed decisions about your future before choosing a major.
          </p>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto" data-testid="career-search-form">
            <div className="bg-white rounded-2xl p-4 shadow-2xl">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={keyword} onChange={e => setKeyword(e.target.value)}
                    placeholder="Search a career (e.g., Software Engineer, Surgeon, Data Scientist)"
                    className="pl-9 h-11 border-[#E2E2E2] text-sm text-foreground" data-testid="career-keyword-input" />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                  <select value={state} onChange={e => setState(e.target.value)}
                    className="h-11 pl-9 pr-4 border border-[#E2E2E2] rounded-md text-sm bg-white text-foreground appearance-none cursor-pointer w-full sm:w-44"
                    data-testid="career-state-select">
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {!emailCaptured && (
                <div className="flex flex-col sm:flex-row gap-3 mt-3 pt-3 border-t border-[#F2F2F0]">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="Enter your email to access insights"
                      className="pl-9 h-11 border-[#E2E2E2] text-sm text-foreground" data-testid="career-email-input" />
                  </div>
                </div>
              )}

              <Button type="submit" disabled={loading}
                className="w-full mt-3 bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-11 text-sm font-semibold"
                data-testid="career-search-btn">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analyzing Career Data...</>
                  : <><Search className="h-4 w-4 mr-2" /> Explore Career Insights</>}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      {data && (
        <section className="max-w-5xl mx-auto px-4 py-12 space-y-8" data-testid="career-results">
          {/* Occupation Header */}
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {data.occupation_title}
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl mx-auto">{data.occupation_summary}</p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Badge variant="outline" className="text-xs">{data.location}</Badge>
              {data.employment_trends?.growth_label && (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                  {data.employment_trends.growth_label}
                </Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Employment Trends */}
            <Card className="border-[#E2E2E2]" data-testid="section-employment-trends">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="h-4.5 w-4.5 text-[#0F392B]" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Employment Trends</h3>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-2xl font-bold">{(data.employment_trends?.current_jobs || 0).toLocaleString()}</span>
                  <span className="text-xs text-muted-foreground">current jobs</span>
                </div>
                <p className="text-xs text-emerald-600 font-medium mb-4">
                  {data.employment_trends?.growth_rate_10yr} projected growth (10yr)
                </p>
                <TrendChart yearData={data.employment_trends?.year_data} />
                <p className="text-xs text-muted-foreground mt-3">{data.employment_trends?.outlook_description}</p>
              </CardContent>
            </Card>

            {/* Annual Earnings */}
            <Card className="border-[#E2E2E2]" data-testid="section-annual-earnings">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="h-4.5 w-4.5 text-[#D4AF37]" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Annual Earnings</h3>
                </div>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-2xl font-bold">${((data.annual_earnings?.median || 0) / 1000).toFixed(0)}k</span>
                  <span className="text-xs text-muted-foreground">median salary</span>
                </div>
                <EarningsCard earnings={data.annual_earnings} />
              </CardContent>
            </Card>

            {/* Technical Skills */}
            <Card className="border-[#E2E2E2]" data-testid="section-technical-skills">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Wrench className="h-4.5 w-4.5 text-[#0F392B]" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Technical Skills in Demand</h3>
                </div>
                <BarChart
                  data={(data.technical_skills || []).map(s => ({ label: s.skill, value: s.demand_level, postfix: '%' }))}
                  maxVal={100}
                  color="#0F392B"
                />
              </CardContent>
            </Card>

            {/* Core Competencies */}
            <Card className="border-[#E2E2E2]" data-testid="section-core-competencies">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="h-4.5 w-4.5 text-[#0F392B]" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Core Competencies</h3>
                </div>
                <div className="space-y-3">
                  {(data.core_competencies || []).map((c, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-28 truncate text-right">{c.competency}</span>
                      <div className="flex-1 h-5 bg-[#F2F2F0] rounded-full overflow-hidden relative">
                        <div className="h-full rounded-full bg-[#0F392B]/70 transition-all duration-700"
                          style={{ width: `${c.level}%` }} />
                      </div>
                      <Badge variant="outline" className="text-[9px] w-14 justify-center">
                        {c.importance}/5
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Education Levels */}
            <Card className="border-[#E2E2E2]" data-testid="section-education-levels">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="h-4.5 w-4.5 text-[#0F392B]" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Education Levels</h3>
                </div>
                <div className="space-y-2">
                  {(data.education_levels || []).map((e, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-36 truncate text-right">{e.level}</span>
                      <div className="flex-1 h-5 bg-[#F2F2F0] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${e.percentage}%`, backgroundColor: i === 0 ? '#D4AF37' : i === 1 ? '#0F392B' : '#0F392B', opacity: 1 - i * 0.12 }} />
                      </div>
                      <span className="text-xs font-medium w-10">{e.percentage}%</span>
                    </div>
                  ))}
                </div>
                {data.recommended_majors && (
                  <div className="mt-4 pt-3 border-t border-[#F2F2F0]">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Recommended Majors</p>
                    <div className="flex flex-wrap gap-1.5">
                      {data.recommended_majors.map((m, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">{m}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Employers */}
            <Card className="border-[#E2E2E2]" data-testid="section-top-employers">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="h-4.5 w-4.5 text-[#0F392B]" />
                  <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Top Employers</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(data.top_employers || []).map((emp, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-[#F9F9F7]">
                      <div className="w-7 h-7 rounded-md bg-[#0F392B]/10 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-3.5 w-3.5 text-[#0F392B]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{emp.name}</p>
                        <p className="text-[9px] text-muted-foreground truncate">{emp.industry}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Career Path & Job Titles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {data.career_path && (
              <Card className="border-[#E2E2E2]" data-testid="section-career-path">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Briefcase className="h-4.5 w-4.5 text-[#0F392B]" />
                    <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Career Path</h3>
                  </div>
                  <div className="space-y-3">
                    {[
                      { label: 'Entry Level', desc: data.career_path.entry, color: '#94a3b8' },
                      { label: 'Mid Career', desc: data.career_path.mid, color: '#0F392B' },
                      { label: 'Senior Level', desc: data.career_path.senior, color: '#D4AF37' },
                    ].map((level, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: level.color }} />
                          {i < 2 && <div className="w-0.5 flex-1 bg-[#E2E2E2] mt-1" />}
                        </div>
                        <div className="pb-3">
                          <p className="text-xs font-semibold">{level.label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{level.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {data.common_job_titles && (
              <Card className="border-[#E2E2E2]" data-testid="section-job-titles">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Briefcase className="h-4.5 w-4.5 text-[#0F392B]" />
                    <h3 className="text-sm font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>Common Job Titles</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.common_job_titles.map((title, i) => (
                      <Badge key={i} variant="outline" className="text-xs py-1.5 px-3">{title}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* CTA */}
          <Card className="border-[#0F392B]/20 bg-[#0F392B]/[0.02]">
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Ready to Build Your Path?
              </h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                Now that you know where you're headed, let us help you get there. Our portal organizes your entire college application — from activities to essays to deadlines.
              </p>
              <Link to="/sign-up">
                <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-11 px-8 text-sm font-semibold" data-testid="results-cta-btn">
                  Start Your Application Journey <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Pre-search content */}
      {!data && !loading && (
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
              What You'll Discover
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Enter any career above to unlock detailed market insights</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { icon: TrendingUp, label: 'Employment Trends', desc: '10-year job growth projections' },
              { icon: DollarSign, label: 'Salary Data', desc: 'Entry to senior-level earnings' },
              { icon: Building2, label: 'Top Employers', desc: 'Companies actively hiring' },
              { icon: Wrench, label: 'In-Demand Skills', desc: 'Technical skills employers want' },
              { icon: BookOpen, label: 'Education Levels', desc: 'Degrees needed for the role' },
              { icon: Briefcase, label: 'Career Path', desc: 'Entry to senior progression' },
            ].map((item, i) => (
              <Card key={i} className="border-[#E2E2E2] hover:border-[#0F392B]/20 transition-colors">
                <CardContent className="p-4 text-center">
                  <item.icon className="h-6 w-6 text-[#0F392B] mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-xs font-medium">{item.label}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-[#E2E2E2] bg-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-[#0F392B]" strokeWidth={1.5} />
            <span className="text-xs text-muted-foreground">Ivy League Consulting</span>
          </div>
          <p className="text-[10px] text-muted-foreground">Career data powered by AI analysis of labor market trends</p>
        </div>
      </footer>
    </div>
  );
}
